import type { ScriptElement, ShapeContent, ShapeKind } from '../../schema';

const VIEWBOX = 100;
const SHAPE_GEOMETRY: Partial<Record<ShapeKind, { kind: 'polygon' | 'path'; data: string }>> = {
  triangle: { kind: 'polygon', data: '50,4 96,96 4,96' },
  diamond: { kind: 'polygon', data: '50,4 96,50 50,96 4,50' },
  pentagon: { kind: 'polygon', data: '50,4 96,38 78,94 22,94 4,38' },
  hexagon: { kind: 'polygon', data: '25,6 75,6 96,50 75,94 25,94 4,50' },
  star: {
    kind: 'polygon',
    data: '50,4 61,38 96,38 68,58 79,92 50,72 21,92 32,58 4,38 39,38',
  },
  arrow: { kind: 'polygon', data: '4,34 60,34 60,12 96,50 60,88 60,66 4,66' },
  heart: {
    kind: 'path',
    data:
      'M50 88 C 14 60, 4 38, 18 22 C 30 8, 46 14, 50 28 C 54 14, 70 8, 82 22 C 96 38, 86 60, 50 88 Z',
  },
  cross: {
    kind: 'polygon',
    data: '38,4 62,4 62,38 96,38 96,62 62,62 62,96 38,96 38,62 4,62 4,38 38,38',
  },
};

/**
 * Chalk-themed shape primitives. Defaults read on the dark-green board
 * (chalk-cream fill, glow), but authored `content.fill` / `content.stroke`
 * win when present.
 */
export function ShapeElement({ element, fill }: { element: ScriptElement; fill?: boolean }) {
  const c = (element.content as ShapeContent | undefined) ?? { shape: 'rect' };
  const fillColor = c.fill ?? (element.style?.backgroundColor as string | undefined) ?? '#3a3128';
  const strokeColor = c.stroke ?? '#3a3128';
  const radius = (element.style?.borderRadius as number | undefined) ?? 8;
  const wrapperStyle: React.CSSProperties = fill
    ? { width: '100%', height: '100%' }
    : { width: '100%', height: 'auto' };
  const glow = `drop-shadow(0 1px 2px rgba(60, 45, 25, 0.25))`;
  const imageFill = c.imageFill?.src ? c.imageFill : null;
  const bg: React.CSSProperties = imageFill
    ? {
        backgroundImage: `url("${imageFill.src}")`,
        backgroundSize: imageFill.objectFit === 'contain' ? 'contain' : 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    : { background: fillColor };
  const patternId = `shapefill-${element.id}`;
  const svgFill = imageFill ? `url(#${patternId})` : fillColor;

  // `borderRadius: '50%'` — deliberately NOT `rounded-full` (9999px). CSS clamps
  // an over-large px radius to half the shorter side, so on a non-square box that
  // yields a stadium/pill rather than an ellipse; a percentage resolves per-axis.
  // Circle and ellipse share this branch — the BOX decides which you get (the
  // editor aspect-locks a circle's box square; an ellipse's stretches freely).
  if (c.shape === 'circle' || c.shape === 'ellipse') {
    return (
      <div
        style={{
          ...bg,
          border: c.stroke ? `2px solid ${strokeColor}` : undefined,
          filter: glow,
          borderRadius: '50%',
          ...wrapperStyle,
        }}
        className={
          fill
            ? 'h-full w-full'
            : c.shape === 'circle'
              ? 'aspect-square h-full w-full max-h-24 max-w-[6rem] self-start'
              : 'h-16 w-full'
        }
      />
    );
  }
  if (c.shape === 'line') {
    return (
      <div
        style={{ background: fillColor, filter: glow, ...wrapperStyle }}
        className="h-1 w-full self-center rounded-full"
      />
    );
  }
  if (c.shape === 'rect') {
    return (
      <div
        style={{
          ...bg,
          borderRadius: radius,
          border: c.stroke ? `2px solid ${strokeColor}` : undefined,
          filter: glow,
          ...wrapperStyle,
        }}
        className={fill ? 'h-full w-full' : 'h-16 w-full'}
      />
    );
  }

  const geometry = SHAPE_GEOMETRY[c.shape];
  if (!geometry) {
    return (
      <div
        style={{ ...bg, borderRadius: radius, filter: glow, ...wrapperStyle }}
        className={fill ? 'h-full w-full' : 'h-16 w-full'}
      />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      preserveAspectRatio="none"
      className={fill ? 'block h-full w-full' : 'block h-full max-h-40 w-full'}
      style={{ filter: glow }}
      aria-label={`${c.shape} shape`}
    >
      {imageFill && (
        <defs>
          <pattern id={patternId} patternUnits="userSpaceOnUse" width={VIEWBOX} height={VIEWBOX}>
            <image
              href={imageFill.src}
              width={VIEWBOX}
              height={VIEWBOX}
              preserveAspectRatio={imageFill.objectFit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
            />
          </pattern>
        </defs>
      )}
      {geometry.kind === 'polygon' ? (
        <polygon
          points={geometry.data}
          fill={svgFill}
          stroke={strokeColor}
          strokeWidth={c.stroke ? 2 : 0}
          strokeLinejoin="round"
          // The viewBox is stretched non-uniformly to fill the box, which would
          // otherwise draw vertical strokes wider than horizontal ones.
          vectorEffect="non-scaling-stroke"
        />
      ) : (
        <path
          d={geometry.data}
          fill={svgFill}
          stroke={strokeColor}
          strokeWidth={c.stroke ? 2 : 0}
          strokeLinejoin="round"
          // The viewBox is stretched non-uniformly to fill the box, which would
          // otherwise draw vertical strokes wider than horizontal ones.
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
